import { ChangeDetectionStrategy, Component, input, output, signal, WritableSignal } from '@angular/core';
import { form, FormField } from '@angular/forms/signals';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatListModule } from '@angular/material/list';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import {
  EntityStatus,
  FrenchAddressRegion,
  PropertyAdminFilterKey,
  PropertyDocumentStatus,
  PropertyOccupationState,
  PropertyType,
  PropertyVerificationStatus,
  ZoudashUtils,
} from '@workspace/data-types';
import {
  AbstractSignalFormComponent,
  DataHeaderFilter,
  FilterChangedDetection,
  FilterRecord,
} from '@workspace/ng-aura';
import { StateStatusDisplayPipe } from '@workspace/ng-data-access';
import { EtxeEnumDisplayPipe } from '../../../pipes/etxe-enum-display.pipe';
import { PropertyColumnKey } from '../abstract-properties-data-table.component';

export interface PropertyFilterModel {
  type: string | string[] | null;
  state: string | string[] | null;
  location: string | string[] | null;
  verificationOwner: string | string[] | null;
  documentStatus: string | string[] | null;
  moderationFlag: string | string[] | null;
}

@Component({
  selector: 'etxe-properties-data-table-filter',
  templateUrl: './properties-data-table-filter.shared.component.html',
  imports: [
    FormField,
    MatFormFieldModule,
    MatInputModule,
    MatListModule,
    MatSlideToggleModule,
    EtxeEnumDisplayPipe,
    StateStatusDisplayPipe,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class PropertiesDataTableFilterCoreComponent extends AbstractSignalFormComponent<
  Partial<Record<PropertyColumnKey, string | string[]>>,
  PropertyFilterModel
> {
  filterChanged = output<FilterChangedDetection>();
  currentFilterView = input<string | null>(null);
  filters = input.required<DataHeaderFilter<PropertyColumnKey>[]>();
  stateValues = Object.values(EntityStatus);

  propertyTypeValues = Object.values(PropertyType);
  propertyOccupationStateValues = Object.values(PropertyOccupationState);
  frenchAddressRegionValues = Object.values(FrenchAddressRegion);
  propertyVerificationStatusValues = Object.values(PropertyVerificationStatus);
  propertyDocumentStatusValues = Object.values(PropertyDocumentStatus);
  propertyAdminFilterKey = PropertyAdminFilterKey;

  override model: WritableSignal<PropertyFilterModel> = signal<PropertyFilterModel>({
    type: null,
    state: null,
    location: null,
    verificationOwner: null,
    documentStatus: null,
    moderationFlag: null,
  });

  override formTree = form(this.model);

  override emitDto(newDto: Partial<Record<PropertyColumnKey, string | string[]>>): void {
    this.filterChanged.emit({
      filter: newDto as FilterRecord,
      changed: !ZoudashUtils.isEqual(newDto, this.ogValue),
    });
  }

  protected override buildModelFromDto(
    dto: Partial<Record<PropertyColumnKey, string | string[]>>,
  ): PropertyFilterModel {
    return {
      type: dto?.['definition.propertyType'] ?? null,
      state: dto?.['state.status'] ?? null,
      location: (dto?.['location.address.region'] as string | string[]) ?? null,
      verificationOwner: (dto?.['verification.owner'] as string | string[]) ?? null,
      documentStatus: (dto?.['documents.status'] as string | string[]) ?? null,
      moderationFlag: (dto?.['moderation.hasContactFlag'] as string | string[]) ?? null,
    };
  }

  protected override buildDtoFromModel(
    model: PropertyFilterModel,
  ): Partial<Record<PropertyColumnKey, string | string[]>> {
    const dto: Partial<Record<string, string | string[]>> = {
      'definition.propertyType': model.type ?? undefined,
      'state.status': model.state ?? undefined,
      'location.address.region': model.location ?? undefined,
      'verification.owner': model.verificationOwner ?? undefined,
      'documents.status': model.documentStatus ?? undefined,
      'moderation.hasContactFlag': model.moderationFlag ?? undefined,
    };
    const filteredEntries = Object.entries(dto).filter(([, v]) => v != null);
    return Object.fromEntries(filteredEntries) as Partial<Record<PropertyColumnKey, string | string[]>>;
  }
}
